<template>
  <header class="app-header" :style="{ backgroundColor: headerBgColor }">
    <div class="header-left">
      <router-link to="/onboarding">
        <img
          alt="Smart Business logo"
          class="logo"
          src="../assets/smart-business-blue.svg"
          width="125"
          height="125"
          style="cursor: pointer"
        />
      </router-link>
    </div>
    <div class="header-right">
      <div v-if="company && !loading" class="session-button" @click="toggleUserMenu">
        <span class="session-text">{{ truncatedCompanyName }}</span>
        <div class="user-avatar">{{ companyName!.charAt(0).toUpperCase() }}</div>
      </div>
      <div v-if="showUserMenu && company" class="user-menu">
        <div class="user-info">
          <span class="user-name">{{ companyName }}</span>
        </div>
        <button @click="handleLogout" class="logout-link">Logout</button>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, onMounted } from 'vue'
import { ApiService } from '../../services/apiService'
import { decodeJwt } from '@/utils/jwtDecoder'
import type { GroupResponseDTO } from 'commons'
import { WebEnvironmentConfigService } from '../../services/env-config.service'

const route = useRoute()
const router = useRouter()
const showUserMenu = ref(false)
const envConfigService = new WebEnvironmentConfigService()
const environment = ref('')
envConfigService
  .getEnvironmentName()
  .then((env) => {
    environment.value = env || ''
  })
  .catch(() => {
    environment.value = ''
  })

const groupId = computed(() => {
  const rawGroupId = (route.params.groupId as string) || 'U'
  const decoded = decodeJwt(rawGroupId)
  if (decoded === null) {
    return null
  }
  const currentTime = Date.now() / 1000
  if (decoded.exp < currentTime) {
    return null
  }
  return decoded.resourceId || ''
})
const company = ref<GroupResponseDTO | null>(null)
const loading = ref(true)

const apiService = new ApiService()

const companyName = computed(() => {
  if (company.value) {
    return company.value.nombrePublico || company.value.descripcion || ''
  }
  return groupId.value
})

const truncatedCompanyName = computed(() => {
  const name = companyName.value || ''
  if (name.length <= 25) {
    return name
  }
  return name.substring(0, 22) + '...'
})

const fetchCompany = async () => {
  try {
    loading.value = true
    company.value = await apiService.getGroup(groupId.value!)
  } catch (err) {
    console.error('Hubo un error: Error fetching company ', err)
  } finally {
    loading.value = false
  }
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const handleLogout = async () => {
  const tokenKey = computed(() => {
    const groupId = route.params.groupId as string
    return decodeJwt(groupId)?.jti || ''
  })
  await apiService.disableAdminAccessTokenKey(tokenKey.value!, groupId.value!)
  router.push('/admin')
}

onMounted(() => {
  if (groupId.value && groupId.value !== 'U') {
    fetchCompany()
  }
})

const headerBgColor = computed(() => {
  console.log('Current environment:', environment.value)
  if (environment.value === 'production') return '#CAE1B8'
  return '#ffffff'
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');

.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  box-shadow: 0 10px 16px 0 rgba(0, 0, 0, 0.1);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 80px;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 40px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
}

.session-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 16px;
  min-width: 200px;
  max-width: 350px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.session-button:hover {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

.session-text {
  flex: 1;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 15px;
  line-height: 130%;
  text-align: center;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #27ae60;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
  flex-shrink: 0;
}

.user-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 12px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  min-width: 200px;
  max-width: 400px;
  z-index: 1001;
}

.user-info {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e4e4;
}

.user-name {
  font-size: 16px;
  color: #555;
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.logout-link {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 0;
  width: 100%;
  text-align: left;
  transition: color 0.2s;
}

.logout-link:hover {
  color: #c82333;
}

.header-left .logo {
  cursor: pointer;
}

.header-left .logo:hover,
.header-left .logo:active,
.header-left .logo:focus {
  outline: none;
  border: none;
  background: none;
  box-shadow: none;
  text-decoration: none;
  filter: none;
}

.header-left a {
  text-decoration: none;
}

.header-left a:focus,
.header-left a:active,
.header-left a:hover {
  outline: none;
  border: none;
  background: none;
  box-shadow: none;
  text-decoration: none;
}

/* Copy URL Button Styles */
.copy-url-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  color: #003896;
  font-weight: 500;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
}

.copy-url-btn:hover {
  background-color: #f5f5f5;
}

.copy-icon {
  width: 20px;
  height: 20px;
}

.copy-text {
  width: 103px;
  height: 25px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  text-align: center;
  color: #1a1a1a;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.content-area {
  padding: 20px;
  width: 100%;
  margin: 20px auto;
  box-sizing: border-box;
  flex-grow: 1;
  padding-top: 100px;
}
</style>
